import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useEffect, useState, useLayoutEffect, lazy, Suspense } from "react";
import HomePage from './pages/HomePage';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import PremiumRoute from './components/Auth/PremiumRoute';
import PrivateAdminRoute from './components/Auth/PrivateAdminRoute';
import PrivateSupportRoute from "./components/Auth/PrivateSupportRoute";
import "../src/components/Authentication/AxiosInterceptor"
import LazyCookiesCompliance from './components/Footer/LazyCookiesCompliance';
import NotFound from './pages/NotFound';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import AOS from "aos";
import "aos/dist/aos.css";

const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const CategoriesFilter = lazy(() => import('./components/CategoriesFilter/CategoriesFilter'));
const ExpertInsightsPage = lazy(() => import('./components/ExpertInsights/ExpertInsightsPage'));
const OpinionsPage = lazy(() => import('./components/Opinions/OpinionsPage'));
const RecommendationsPage = lazy(() => import('./components/RecommendationsSection/RecommendationsPage'));
const TrendingPage = lazy(() => import('./components/TrendingSection/TrendingPage'));
const LatestNewsPage = lazy(() => import('./components/LatestNews/LatestNewsPage'));
const BecomeAuthor = lazy(() => import('./components/BecomeAuthor/BecomeAuthor'));
const LoginPage = lazy(() => import('./components/Authentication/LoginPage'));
const SignupPage = lazy(() => import('./components/Authentication/SignupPage'));
const SSOCallback = lazy(() => import('./components/Authentication/SSOCallback'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const ArticleCreationForm = lazy(() => import('./components/Articles/ArticleCreationForm'));
const MyArticles = lazy(() => import('./pages/MyArticlesPage'));
const ForgotPassword = lazy(() => import('./components/Authentication/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/Authentication/ResetPassword'));
const SubscriptionPage = lazy(() => import('./components/Dashboard/SubscriptionPage'));
const MyInterestsPage = lazy(() => import('./pages/MyInterestsPage'));
const InterviewDetail = lazy(() => import('./components/ExclusiveInterviews/InteviewDetails'));
const IoCReportingForm = lazy(() => import('./components/ThreatIntelligence/IoCReportingForm'));
const ThreatActorDetails = lazy(() => import('./components/ThreatIntelligence/ThreatActorDetails'));
const MitreInfo = lazy(() => import('./components/ThreatIntelligence/MitreInfo'));
const ApiKeyInfo = lazy(() => import('./components/ThreatIntelligence/ApiKeyInfo'));
const AlertsPage = lazy(() => import('./components/Neutron/Alerts'));
const Neutron = lazy(() => import('./components/Neutron/Neutron'));
const ReportForm = lazy(() => import('./pages/UserReportingPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const PlatformUsage = lazy(() => import('./components/Footer/PlatformUsage'));
const ResearcherApplicationForm = lazy(() => import('./components/Footer/ResearcherApplicationForm'));
const TermsAndConditions = lazy(() => import('./components/Footer/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./components/Footer/PrivacyPolicy'));
const FAQsPage = lazy(() => import('./components/Footer/FAQsPage'));
const AboutUs = lazy(() => import('./components/Footer/AboutUs'));
const ContactUs = lazy(() => import('./components/Footer/ContactUs'));
const CareersPage = lazy(() => import('./components/Footer/CareersPage'));
const ApplyPage = lazy(() => import('./components/Career/ApplyPage'));
const SuccessPage = lazy(() => import('./components/Career/SuccessPage'));
const PlatformOverviewPage = lazy(() => import('./components/Footer/PlatformOverview'));
const SubmitResearch = lazy(() => import('./components/Footer/SubmitResearch'));
const AdminPanel = lazy(() => import('./components/AdminDashboard/AdminPanel'));
const SSOOrgsPage = lazy(() => import('./components/AdminDashboard/SSOOrgsPage'));
const PartnershipForm = lazy(() => import('./components/Footer/PartnershipForm'));
const PressMediaList = lazy(() => import('./components/Footer/PressMediaList'));
const PressMediaDetail = lazy(() => import('./components/Footer/PressMediaDetail'));
const CookiePreferences = lazy(() => import('./components/Dashboard/CookiePreferences'));
const EmergingTechInitiatives = lazy(() => import('./components/Initiatives/EmergingTechInitiatives'));
const EmergingTechInitiativeDetail = lazy(() => import('./components/Initiatives/EmergingTechInitiativeDetail'));
const ForumsPage = lazy(() => import('./components/Forums/ForumsPage'));
const AskQuestionPage = lazy(() => import('./components/Forums/AskQuestionPage'));
const ThreadPage = lazy(() => import('./components/Forums/ThreadPage'));
const InvestigationsPage = lazy(() => import('./pages/InvestigationsPage'));
const CHIRPProgramPage = lazy(() => import('./components/Chirp/CHIRPProgramPage'));
const IntelligenceModeratorPanel = lazy(() => import('./components/ModeratorDashboard/IntelligenceModeratorPanel'));
const PublicBadgePage = lazy(() => import('./components/Chirp/PublicBadgePage'));
const NeutronDownload = lazy(() => import('./components/Neutron/NeutronDownload'));
const NeutronHelpCenter = lazy(() => import('./components/Neutron/NeutronHelpCenter'));
const TrialRequestPage = lazy(() => import('./components/PremiumTrial/TrialRequestPage'));
const EditArticlePage = lazy(() => import('./components/Articles/EditArticlePage'));
const SupportTicketsPage = lazy(() => import('./pages/SupportTicketsPage'));
const SupportAdminPage = lazy(() => import('./pages/SupportAdminPage'));
const AuthorPage = lazy(() => import('./pages/AuthorPage'));
const CyberArenaPage = lazy(() => import('./pages/CyberArenaPage'));
const CyberRankPage = lazy(() => import('./pages/CyberRankPage'));
const NeuraCybIntelPage = lazy(() => import('./pages/NeuraCybIntelPage'));

function App() {
  const isAuthed = !!localStorage.getItem("token");

  const getPreferredTheme = () => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "light" || savedTheme === "dark") {
      return savedTheme;
    }

    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  };

  const [theme] = useState(getPreferredTheme);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useLayoutEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
    <div className="app-shell">
      <Router>
        <Header />
        <LazyCookiesCompliance />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/articles/:slug" element={<ArticlePage />} />
            <Route path="/articles" element={<LatestNewsPage />} />
            <Route path="/category/:categoryName" element={<CategoriesFilter />} />
            <Route path="/articles/expert-insights" element={<ExpertInsightsPage />} />
            <Route path="/articles/trending" element={<TrendingPage />} />
            <Route path="/articles/opinions" element={<OpinionsPage />} />
            <Route path="/articles/recommendations" element={<RecommendationsPage />} />
            <Route path="/articles/latest-news" element={<LatestNewsPage />} />
            <Route path="/become-author" element={<BecomeAuthor />} />
            <Route path="/login" element={isAuthed ? <Navigate to="/" replace /> : <LoginPage />} />
            <Route path="/signup" element={isAuthed ? <Navigate to="/" replace /> : <SignupPage />} />
            <Route path="/sso-callback" element={<SSOCallback />} />
            <Route path="/my-profile" element={<Dashboard />} />
            <Route path="/create-article" element={<ArticleCreationForm />} />
            <Route path="/my-articles" element={<MyArticles />} />
            <Route path='/forgot-password' element={<ForgotPassword />} />
            <Route path='/reset-password/:token' element={<ResetPassword />} />
            <Route path='/subscription' element={<SubscriptionPage />} />
            <Route path='/interviews/:id' element={<InterviewDetail />} />
            <Route path='/cyber-intelligence' element={<MyInterestsPage />} />
            <Route path='/report-ioc' element={<IoCReportingForm />} />
            <Route path='/threat-actors/:id' element={<ThreatActorDetails />} />
            <Route path='/mitre-info' element={<MitreInfo />} />
            <Route path='/api-key-info' element={<ApiKeyInfo />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/neuramon" element={<Neutron />} />
            <Route path='/report' element={<ReportForm />} />
            <Route path="/search-results" element={<SearchResultsPage />} />
            <Route path="/platform-usage" element={<PlatformUsage />} />
            <Route path="/apply/researcher" element={<ResearcherApplicationForm />} />
            <Route path="/legal/terms" element={<TermsAndConditions />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/help/faqs" element={<FAQsPage />} />
            <Route path='/company/about' element={<AboutUs />} />
            <Route path='/company/contact' element={<ContactUs />} />
            <Route path='/careers/opportunities' element={<CareersPage />} />
            <Route path='/apply/:jobId' element={<ApplyPage />} />
            <Route path='/success/:applicationId' element={<SuccessPage />} />
            <Route path='/platform/overview' element={<PlatformOverviewPage />} />
            <Route path='/publish-research' element={<SubmitResearch />} />
            <Route path='/collaborations' element={<PartnershipForm />} />
            <Route path='/media/press' element={<PressMediaList />} />
            <Route path='/press-media/:id' element={<PressMediaDetail />} />
            <Route path='/compliance/cookies' element={<CookiePreferences />} />
            <Route path='/initiatives' element={<EmergingTechInitiatives />} />
            <Route path='/initiatives/:id' element={<EmergingTechInitiativeDetail />} />
            <Route path='/community/forums' element={<ForumsPage />} />
            <Route path='/community/forums/ask' element={<AskQuestionPage />} />
            <Route path='/community/forums/thread/:id' element={<ThreadPage />} />
            <Route path='/investigation-tools' element={<InvestigationsPage />} />
            <Route path='/neuramon' element={<Neutron />} />
            <Route path="/chirp-program" element={<CHIRPProgramPage />} />
            <Route path="/cyber-arena" element={<CyberArenaPage />} />
            <Route path="/cyber-rank" element={<CyberRankPage />} />
            <Route path="/badge/:username" element={<PublicBadgePage />} />
            <Route path="/neutron-agent-download" element={<NeutronDownload />} />
            <Route path="/help/neutron-agent" element={<NeutronHelpCenter />} />
            <Route path="/pricing" element={<SubscriptionPage />} />
            <Route path="/start-trial" element={<TrialRequestPage />} />
            <Route path="/edit-article/:id" element={<EditArticlePage />} />
            <Route path="/support/tickets" element={<SupportTicketsPage />} />
            <Route path="/authors/:slug" element={<AuthorPage />} />
            <Route path="/investigations/ioc/:ioc" element={<NeuraCybIntelPage />} />

            <Route element={<PremiumRoute />}>
              <Route path="/neuracyb-intel-admin" element={<PrivateAdminRoute><AdminPanel /></PrivateAdminRoute>} />
              <Route path="/admin/sso-organizations" element={<PrivateAdminRoute><SSOOrgsPage /></PrivateAdminRoute>} />
              <Route path="/neuracyb-intel-moderator" element={<PrivateAdminRoute><IntelligenceModeratorPanel /></PrivateAdminRoute>} />
              <Route path="/admin/support-tickets" element={<PrivateSupportRoute><SupportAdminPage /></PrivateSupportRoute>} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Footer />
      </Router>
    </div>
    </GoogleOAuthProvider>
  );
}

export default App;
